import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  InputAdornment,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AddLocationAltIcon from '@mui/icons-material/AddLocationAlt';

import {
  previewBulkZipUpload,
  commitBulkZipUpload,
  assistBulkZipMapping,
  downloadZipTemplate,
  fetchZipLaunchContext,
} from '@src/api/actions/dashboard/zipSubmissions';

const SKIP = '__skip__';
const CREATE = '__create__';
const NOT_PRESENT = '__none__';

const ROLE_LABELS = {
  client: 'Client',
  territory: 'Territory',
  vertical: 'Vertical',
  zip: 'ZIP',
  action: 'Action',
};

// Server-driven bulk upload: paste/upload a CSV, the backend guesses which
// column is which and fuzzy-matches client/territory/vertical names, and
// anything it couldn't resolve comes back as an issue with suggestions the
// user remaps here. Every change re-runs the preview; Submit only unlocks
// when the server says the upload is clean.
export default function BulkZipUploadDialog({
  open,
  onClose,
  clientId,
  parentCompanyId,
  territories = [],
  allVerticals = [],
  clientOptions = [],
  onSubmitted,
}) {
  const isParentScope = Boolean(parentCompanyId) && !clientId;

  const [csvText, setCsvText] = useState('');
  const [fileName, setFileName] = useState('');
  const [notes, setNotes] = useState('');
  // "When should these markets start?" — blank means as soon as approved.
  const [launchOn, setLaunchOn] = useState('');
  // Monthly amount per new market — only asked for a single client who
  // budgets by territory (each new territory's segment is drafted with it).
  // Parent-scope uploads span clients, so the amount is set per client in
  // Budget Center instead.
  const [launchAmount, setLaunchAmount] = useState('');
  const [launchContext, setLaunchContext] = useState(null);
  useEffect(() => {
    if (!launchOn || !clientId) {
      setLaunchContext(null);
      return undefined;
    }
    let cancelled = false;
    fetchZipLaunchContext({ clientId, startOn: launchOn })
      .then((res) => { if (!cancelled) setLaunchContext(res?.data || null); })
      .catch(() => { if (!cancelled) setLaunchContext(null); });
    return () => { cancelled = true; };
  }, [launchOn, clientId]);
  const asksForLaunchAmount = Boolean(launchOn && clientId && launchContext?.budgets_by_territory);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [columnMap, setColumnMap] = useState({});
  const [clientMap, setClientMap] = useState({});
  const [territoryMap, setTerritoryMap] = useState({});
  const [verticalMap, setVerticalMap] = useState({});
  const [dropInvalidZips, setDropInvalidZips] = useState(false);
  const [defaultAction, setDefaultAction] = useState('add');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiMatches, setAiMatches] = useState([]);
  const [aiMessage, setAiMessage] = useState('');
  const fileInputRef = useRef(null);
  const previewTimerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setCsvText('');
    setFileName('');
    setNotes('');
    setPreview(null);
    setError('');
    setSubmitting(false);
    setColumnMap({});
    setClientMap({});
    setTerritoryMap({});
    setVerticalMap({});
    setDropInvalidZips(false);
    setDefaultAction('add');
    setAiLoading(false);
    setAiMatches([]);
    setAiMessage('');
  }, [open]);

  const scopePayload = useMemo(() => (
    clientId
      ? { client_id: Number(clientId) }
      : { parent_company_id: Number(parentCompanyId) }
  ), [clientId, parentCompanyId]);

  const buildPayload = useCallback(() => ({
    ...scopePayload,
    csv_text: csvText,
    column_map: columnMap,
    client_map: clientMap,
    territory_map: territoryMap,
    vertical_map: verticalMap,
    drop_invalid_zips: dropInvalidZips,
    default_action: defaultAction,
  }), [
    scopePayload, csvText, columnMap, clientMap, territoryMap, verticalMap,
    dropInvalidZips, defaultAction,
  ]);

  // Debounced server preview on any input change.
  useEffect(() => {
    if (!open) return undefined;
    if (!csvText.trim()) {
      setPreview(null);
      setError('');
      return undefined;
    }
    if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
    previewTimerRef.current = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const res = await previewBulkZipUpload(buildPayload());
        setPreview(res.data);
        setError('');
      } catch (err) {
        setPreview(null);
        setError(err?.response?.data?.detail || 'Failed to read the file.');
      } finally {
        setPreviewLoading(false);
      }
    }, 500);
    return () => {
      if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
    };
  }, [open, csvText, buildPayload]);

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      setCsvText(event.target?.result || '');
      setFileName(file.name);
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await downloadZipTemplate(
        clientId ? { client_id: clientId } : { parent_company_id: parentCompanyId }
      );
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'zip_upload_template.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError('Failed to download template.');
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');
    try {
      const res = await commitBulkZipUpload({
        ...buildPayload(),
        submitter_notes: notes.trim(),
        start_on: launchOn || undefined,
        start_amount: asksForLaunchAmount && launchAmount ? launchAmount.replace(/,/g, '') : undefined,
      });
      onSubmitted?.(res.data);
      onClose();
    } catch (err) {
      const data = err?.response?.data || {};
      if (data.preview) setPreview(data.preview);
      setError(data.detail || 'Failed to submit the upload.');
    } finally {
      setSubmitting(false);
    }
  };

  const headers = preview?.headers || [];
  const roles = isParentScope
    ? ['client', 'territory', 'vertical', 'zip', 'action']
    : ['territory', 'vertical', 'zip', 'action'];

  const territoriesByClient = useMemo(() => {
    const map = {};
    territories.forEach((t) => {
      const cid = t.client;
      if (!map[cid]) map[cid] = [];
      map[cid].push(t);
    });
    return map;
  }, [territories]);

  const issueCount = preview
    ? (preview.errors?.length || 0)
      + (preview.unmatched_clients?.length || 0)
      + (preview.unmatched_territories?.length || 0)
      + (preview.unmatched_verticals?.length || 0)
      + (preview.invalid_zips?.length && !dropInvalidZips ? 1 : 0)
    : 0;

  // Names the AI assist can act on (it skips "no vertical in CSV" issues —
  // those are a human choice, not a matching problem).
  const aiEligibleCount = preview
    ? (preview.unmatched_clients?.length || 0)
      + (preview.unmatched_territories?.length || 0)
      + (preview.unmatched_verticals || []).filter(
        (i) => !String(i.key).startsWith('__missing__::')
      ).length
    : 0;

  const handleAiAssist = async () => {
    setAiLoading(true);
    setAiMessage('');
    try {
      const res = await assistBulkZipMapping(buildPayload());
      const data = res.data || {};
      const found = [];

      const clientNameById = {};
      (preview?.unmatched_clients || []).forEach((i) => i.suggestions.forEach((s) => { clientNameById[s.id] = s.name; }));
      clientOptions.forEach((o) => { clientNameById[Number(o.value)] = o.label; });
      const territoryNameById = {};
      territories.forEach((t) => { territoryNameById[t.id] = t.name; });
      const verticalNameById = {};
      allVerticals.forEach((v) => { verticalNameById[v.id] = v.vertical; });

      const issueByKey = (list) => Object.fromEntries((list || []).map((i) => [i.key, i]));
      const clientIssues = issueByKey(preview?.unmatched_clients);
      const territoryIssues = issueByKey(preview?.unmatched_territories);
      const verticalIssues = issueByKey(preview?.unmatched_verticals);

      const cPicks = {};
      Object.entries(data.client_map || {}).forEach(([key, id]) => {
        if (clientMap[key] !== undefined) return; // user's pick wins
        cPicks[key] = id;
        found.push({ kind: 'client', key, from: clientIssues[key]?.name || key, to: clientNameById[id] || `#${id}` });
      });
      const tPicks = {};
      Object.entries(data.territory_map || {}).forEach(([key, id]) => {
        if (territoryMap[key] !== undefined) return;
        tPicks[key] = id;
        found.push({
          kind: 'territory', key,
          from: territoryIssues[key]?.name || key,
          to: territoryNameById[id] || `#${id}`,
          context: territoryIssues[key]?.context,
        });
      });
      const vPicks = {};
      Object.entries(data.vertical_map || {}).forEach(([key, id]) => {
        if (verticalMap[key] !== undefined) return;
        vPicks[key] = id;
        found.push({ kind: 'vertical', key, from: verticalIssues[key]?.name || key, to: verticalNameById[id] || `#${id}` });
      });

      if (Object.keys(cPicks).length) setClientMap((prev) => ({ ...cPicks, ...prev }));
      if (Object.keys(tPicks).length) setTerritoryMap((prev) => ({ ...tPicks, ...prev }));
      if (Object.keys(vPicks).length) setVerticalMap((prev) => ({ ...vPicks, ...prev }));

      if (found.length) {
        setAiMatches((prev) => [...prev, ...found]);
        if (data.unresolved > 0) {
          setAiMessage(`AI matched ${found.length} — ${data.unresolved} still need a manual pick below.`);
        }
      } else {
        setAiMessage("AI couldn't confidently match the remaining names — pick them manually below.");
      }
    } catch (err) {
      setAiMessage(err?.response?.data?.detail || 'AI matching is unavailable right now — use the dropdowns instead.');
    } finally {
      setAiLoading(false);
    }
  };

  const handleUndoAiMatch = (match) => {
    const strip = (setter) => setter((prev) => {
      const next = { ...prev };
      delete next[match.key];
      return next;
    });
    if (match.kind === 'client') strip(setClientMap);
    if (match.kind === 'territory') strip(setTerritoryMap);
    if (match.kind === 'vertical') strip(setVerticalMap);
    setAiMatches((prev) => prev.filter((m) => m !== match));
  };

  const renderColumnMapping = () => (
    <Box>
      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
        Column Mapping
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        We guessed which column is which — fix any that are wrong.
      </Typography>
      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
        {roles.map((role) => {
          const guessed = preview?.column_guesses?.[role]?.header;
          const value = columnMap[role] !== undefined
            ? columnMap[role]
            : (preview?.columns?.[role] || NOT_PRESENT);
          const required = role === 'zip' || role === 'territory' || (role === 'client' && isParentScope);
          return (
            <FormControl key={role} size="small" sx={{ minWidth: 170 }} error={required && value === NOT_PRESENT}>
              <InputLabel>{ROLE_LABELS[role]}{required ? ' *' : ''}</InputLabel>
              <Select
                label={`${ROLE_LABELS[role]}${required ? ' *' : ''}`}
                value={value === null ? NOT_PRESENT : value}
                onChange={(e) => setColumnMap((prev) => ({
                  ...prev,
                  [role]: e.target.value === NOT_PRESENT ? SKIP : e.target.value,
                }))}
              >
                <MenuItem value={NOT_PRESENT}>
                  <em>Not in file</em>
                </MenuItem>
                {headers.filter(Boolean).map((h) => (
                  <MenuItem key={h} value={h}>
                    {h}{h === guessed ? ' ✨' : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          );
        })}
      </Stack>
    </Box>
  );

  const renderFuzzyMatches = () => {
    const matches = preview?.fuzzy_matches || [];
    if (!matches.length) return null;
    return (
      <Alert severity="info" icon={<AutoFixHighIcon fontSize="inherit" />}>
        <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
          Smart-matched names
        </Typography>
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          {matches.map((m, i) => (
            <Chip
              key={i}
              size="small"
              variant="outlined"
              label={`${m.from} → ${m.to}${m.context ? ` (${m.context})` : ''}`}
            />
          ))}
        </Stack>
      </Alert>
    );
  };

  const renderAiAssist = () => {
    if (!aiEligibleCount && !aiMatches.length && !aiMessage) return null;
    return (
      <Stack spacing={1}>
        {aiEligibleCount > 0 && (
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
            <Button
              size="small"
              variant="outlined"
              startIcon={aiLoading ? <CircularProgress size={14} /> : <AutoAwesomeIcon />}
              onClick={handleAiAssist}
              disabled={aiLoading || previewLoading}
            >
              {aiLoading ? 'Matching…' : `Ask AI to match ${aiEligibleCount} name${aiEligibleCount > 1 ? 's' : ''}`}
            </Button>
            <Typography variant="caption" color="text.secondary">
              AI compares the file&apos;s names against your real clients, territories, and verticals — you review every pick before submitting.
            </Typography>
          </Stack>
        )}
        {aiMessage && (
          <Typography variant="caption" color="text.secondary">{aiMessage}</Typography>
        )}
        {aiMatches.length > 0 && (
          <Alert severity="success" icon={<AutoAwesomeIcon fontSize="inherit" />}>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
              AI-matched names — click ✕ to undo one
            </Typography>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {aiMatches.map((m, i) => (
                <Chip
                  key={`${m.kind}-${m.key}-${i}`}
                  size="small"
                  variant="outlined"
                  label={`${m.from} → ${m.to}${m.context ? ` (${m.context})` : ''}`}
                  onDelete={() => handleUndoAiMatch(m)}
                />
              ))}
            </Stack>
          </Alert>
        )}
      </Stack>
    );
  };

  const renderUnmatchedClients = () => {
    const issues = preview?.unmatched_clients || [];
    if (!issues.length) return null;
    return (
      <Alert severity="warning">
        <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
          {issues.length} client name{issues.length > 1 ? 's' : ''} in the file didn&apos;t match any client
        </Typography>
        <Stack spacing={1}>
          {issues.map((issue) => (
            <Stack key={issue.key} direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="body2" sx={{ minWidth: 180 }}>
                &quot;{issue.name}&quot; <Typography component="span" variant="caption" color="text.secondary">({issue.row_count} rows)</Typography>
              </Typography>
              <FormControl size="small" sx={{ minWidth: 240 }}>
                <InputLabel>Map to client</InputLabel>
                <Select
                  label="Map to client"
                  value={clientMap[issue.key] ?? ''}
                  onChange={(e) => setClientMap((prev) => ({ ...prev, [issue.key]: e.target.value }))}
                >
                  {issue.suggestions.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name} <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>({Math.round(s.score * 100)}% match)</Typography>
                    </MenuItem>
                  ))}
                  {clientOptions
                    .filter((o) => !issue.suggestions.some((s) => String(s.id) === String(o.value)))
                    .map((o) => (
                      <MenuItem key={o.value} value={Number(o.value)}>{o.label}</MenuItem>
                    ))}
                  <Divider />
                  <MenuItem value={SKIP}><em>Skip these rows</em></MenuItem>
                </Select>
              </FormControl>
            </Stack>
          ))}
        </Stack>
      </Alert>
    );
  };

  const renderUnmatchedTerritories = () => {
    const issues = preview?.unmatched_territories || [];
    if (!issues.length) return null;
    return (
      <Alert severity="warning">
        <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
          {issues.length} territory name{issues.length > 1 ? 's' : ''} didn&apos;t match
        </Typography>
        <Stack spacing={1}>
          {issues.map((issue) => {
            const issueClientId = Number(issue.key.split('::')[0]);
            const clientTerritories = territoriesByClient[issueClientId] || [];
            return (
              <Stack key={issue.key} direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography variant="body2" sx={{ minWidth: 180 }}>
                  &quot;{issue.name}&quot;
                  {issue.context && (
                    <Typography component="span" variant="caption" color="text.secondary"> — {issue.context}</Typography>
                  )}
                  <Typography component="span" variant="caption" color="text.secondary"> ({issue.row_count} rows)</Typography>
                </Typography>
                <FormControl size="small" sx={{ minWidth: 260 }}>
                  <InputLabel>Map to territory</InputLabel>
                  <Select
                    label="Map to territory"
                    value={territoryMap[issue.key] ?? ''}
                    onChange={(e) => setTerritoryMap((prev) => ({ ...prev, [issue.key]: e.target.value }))}
                  >
                    {issue.suggestions.map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        {s.name} <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>({Math.round(s.score * 100)}% match)</Typography>
                      </MenuItem>
                    ))}
                    {clientTerritories
                      .filter((t) => !issue.suggestions.some((s) => s.id === t.id))
                      .map((t) => (
                        <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                      ))}
                    <Divider />
                    <MenuItem value={CREATE}>
                      <AddLocationAltIcon fontSize="small" sx={{ mr: 0.75 }} />
                      Create new territory &quot;{issue.name}&quot;
                    </MenuItem>
                    <MenuItem value={SKIP}><em>Skip these rows</em></MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            );
          })}
        </Stack>
      </Alert>
    );
  };

  const renderUnmatchedVerticals = () => {
    const issues = preview?.unmatched_verticals || [];
    if (!issues.length) return null;
    return (
      <Alert severity="warning">
        <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
          {issues.length} vertical{issues.length > 1 ? 's' : ''} need{issues.length > 1 ? '' : 's'} a match
        </Typography>
        <Stack spacing={1}>
          {issues.map((issue) => {
            const optionList = issue.options?.length ? issue.options : issue.suggestions;
            return (
              <Stack key={issue.key} direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography variant="body2" sx={{ minWidth: 180 }}>
                  {issue.name === '(no vertical in CSV)' ? issue.name : `"${issue.name}"`}
                  {issue.context && (
                    <Typography component="span" variant="caption" color="text.secondary"> — {issue.context}</Typography>
                  )}
                  <Typography component="span" variant="caption" color="text.secondary"> ({issue.row_count} rows)</Typography>
                </Typography>
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel>Map to vertical</InputLabel>
                  <Select
                    label="Map to vertical"
                    value={verticalMap[issue.key] ?? ''}
                    onChange={(e) => setVerticalMap((prev) => ({ ...prev, [issue.key]: e.target.value }))}
                  >
                    {optionList.map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        {s.name}
                        {s.score != null && (
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>({Math.round(s.score * 100)}% match)</Typography>
                        )}
                      </MenuItem>
                    ))}
                    {allVerticals
                      .filter((v) => !optionList.some((s) => s.id === v.id))
                      .map((v) => (
                        <MenuItem key={v.id} value={v.id}>{v.vertical}</MenuItem>
                      ))}
                    <Divider />
                    <MenuItem value={SKIP}><em>Skip these rows</em></MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            );
          })}
        </Stack>
      </Alert>
    );
  };

  const renderInvalidZips = () => {
    const invalid = preview?.invalid_zips || [];
    if (!invalid.length) return null;
    return (
      <Alert severity={dropInvalidZips ? 'info' : 'warning'}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <Typography variant="body2">
            {invalid.length} ZIP{invalid.length > 1 ? 's' : ''} not recognized:{' '}
            {invalid.slice(0, 10).join(', ')}{invalid.length > 10 ? ` +${invalid.length - 10} more` : ''}
          </Typography>
          <FormControlLabel
            control={(
              <Switch
                size="small"
                checked={dropInvalidZips}
                onChange={(e) => setDropInvalidZips(e.target.checked)}
              />
            )}
            label={<Typography variant="caption">Ignore these rows</Typography>}
          />
        </Stack>
      </Alert>
    );
  };

  const renderGroups = () => {
    const groups = preview?.groups || [];
    if (!groups.length) return null;
    return (
      <Box>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          What will be submitted
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              {isParentScope && <TableCell>Client</TableCell>}
              <TableCell>Territory</TableCell>
              <TableCell>Vertical</TableCell>
              <TableCell align="right">Adds</TableCell>
              <TableCell align="right">Removes</TableCell>
              <TableCell align="right">No change</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groups.map((g) => (
              <TableRow key={`${g.client_id}-${g.territory_key}-${g.vertical_id}`}>
                {isParentScope && <TableCell>{g.client_name}</TableCell>}
                <TableCell>
                  {g.territory_name}
                  {g.create_territory && (
                    <Chip label="new" size="small" color="info" sx={{ ml: 0.75, height: 18 }} />
                  )}
                  {g.mode === 'replace' && (
                    <Tooltip title="Replace: your file becomes the complete ZIP list for this territory/vertical. Other territories are not affected.">
                      <Chip label="replace" size="small" color="warning" variant="outlined" sx={{ ml: 0.75, height: 18 }} />
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell>{g.vertical_name}</TableCell>
                <TableCell align="right">{g.adds.length ? `+${g.adds.length}` : '—'}</TableCell>
                <TableCell align="right">{g.removes.length ? `−${g.removes.length}` : '—'}</TableCell>
                <TableCell align="right">
                  {g.unchanged ? (
                    <Tooltip title="Already active (adds) or not currently active (removes) — no submission needed">
                      <span>{g.unchanged}</span>
                    </Tooltip>
                  ) : '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        Bulk ZIP Upload
        {isParentScope && (
          <Typography variant="body2" color="text.secondary">
            Across all clients in this parent company — include a Client column in your file.
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Button
              variant="outlined"
              size="small"
              startIcon={<UploadFileIcon />}
              onClick={() => fileInputRef.current?.click()}
            >
              Upload CSV
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.tsv,.txt"
              hidden
              onChange={handleFileUpload}
            />
            <Button size="small" startIcon={<DownloadIcon />} onClick={handleDownloadTemplate}>
              Download template
            </Button>
            {fileName && <Chip label={fileName} size="small" onDelete={() => { setCsvText(''); setFileName(''); }} />}
            {previewLoading && <CircularProgress size={18} />}
          </Stack>

          <TextField
            multiline
            minRows={3}
            maxRows={8}
            placeholder={`Or paste CSV here — headers first, e.g.\n${isParentScope ? 'Client,' : ''}Territory,Vertical,ZIP,Action`}
            value={csvText}
            onChange={(e) => { setCsvText(e.target.value); setFileName(''); }}
            fullWidth
          />

          <Typography variant="caption" color="text.secondary">
            <strong>Action column:</strong> <strong>Add</strong> appends ZIPs (default when blank) ·{' '}
            <strong>Remove</strong> drops the listed ZIPs · <strong>Replace</strong> makes your file the
            complete list for that territory/vertical. Your file only ever affects the
            territories and verticals it mentions — everything else is untouched, so
            there&apos;s no need to re-upload your full coverage.
          </Typography>

          {error && <Alert severity="error" sx={{ whiteSpace: 'pre-line' }}>{error}</Alert>}

          {preview && (
            <>
              {renderColumnMapping()}
              {(preview.errors || []).map((msg, i) => (
                <Alert key={i} severity="error">{msg}</Alert>
              ))}
              {renderFuzzyMatches()}
              {renderAiAssist()}
              {renderUnmatchedClients()}
              {renderUnmatchedTerritories()}
              {renderUnmatchedVerticals()}
              {renderInvalidZips()}
              {renderGroups()}

              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                <Typography variant="caption" color="text.secondary">
                  {preview.total_rows} rows · +{preview.total_adds} adds · −{preview.total_removes} removes
                  {preview.skipped_rows ? ` · ${preview.skipped_rows} skipped` : ''}
                </Typography>
                {preview.ready ? (
                  <Chip label="Ready to submit" color="success" size="small" />
                ) : (
                  <Chip
                    label={issueCount ? 'Fix the issues above to continue' : 'Nothing to submit'}
                    color="warning"
                    size="small"
                  />
                )}
              </Stack>

              <TextField
                label="Notes for the reviewer (optional)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                size="small"
                fullWidth
              />
              <TextField
                type="date"
                label="When should these markets start? (optional)"
                helperText="Leave blank to go live as soon as approved. With a date, the ZIPs are held until then. If you budget by territory, each territory's segment is drafted for you to fund."
                value={launchOn}
                onChange={(e) => setLaunchOn(e.target.value)}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              {asksForLaunchAmount ? (
                <TextField
                  label={`Monthly budget per new market — ${launchContext?.month_label || ''} (optional)`}
                  helperText="You budget by territory, so each new market gets its own segment. This amount is drafted for every new territory in this upload, queued for approval. Leave blank to set them in Budget Center later."
                  placeholder="e.g. 5000"
                  value={launchAmount}
                  onChange={(e) => setLaunchAmount(e.target.value.replace(/[^0-9.,]/g, ''))}
                  size="small"
                  fullWidth
                  inputProps={{ inputMode: 'decimal' }}
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                />
              ) : null}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!preview?.ready || submitting || previewLoading}
        >
          {submitting ? 'Submitting…' : 'Submit for Review'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

BulkZipUploadDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  clientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  parentCompanyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  territories: PropTypes.arrayOf(PropTypes.object),
  allVerticals: PropTypes.arrayOf(PropTypes.object),
  clientOptions: PropTypes.arrayOf(PropTypes.object),
  onSubmitted: PropTypes.func,
};
